// Damit sich die PWA wie eine App anfühlt und nicht wie eine Webseite.
//
// Das meiste erledigt CSS (index.css: overflow, overscroll-behavior,
// touch-action). Hier steht nur der Rest — die Dinge, die iOS Safari bewusst
// NICHT über CSS abstellen lässt.
//
// ══ Warum iOS eine Sonderbehandlung braucht ══
//
// Apple ignoriert `user-scalable=no` und `maximum-scale` seit iOS 10 absichtlich:
// Webseiten sollten das Zoomen nicht kaputtmachen dürfen. Als Grundhaltung
// richtig — nur trifft es hier auch die installierte App, die sich wie eine App
// verhalten SOLL. Der einzige Weg dorthin sind die `gesture*`-Events (nicht
// standardisiert, aber seit Jahren stabil).
//
// Das ist bewusst der EINZIGE Ort mit solchen Griffen: Wer später etwas ändert,
// findet alles beisammen statt verstreut in fünf Komponenten.

/** Läuft die App als installierte PWA (nicht in einem Browser-Tab)? */
export function istInstalliert(): boolean {
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    // iOS meldet das nicht über display-mode, sondern über eine eigene Flag.
    (navigator as Navigator & { standalone?: boolean }).standalone === true
  );
}

export function nativesGefuehlAktivieren() {
  // ── 1. Pinch-Zoom (iOS)
  //
  // `gesturestart` feuert, sobald zwei Finger aufsetzen. Es abzufangen ist der
  // einzige Weg, das Zoomen in Safari zu unterbinden. In allen anderen Browsern
  // gibt es das Event nicht — der Listener läuft dort schlicht nie.
  const gesteAbfangen = (e: Event) => e.preventDefault();
  document.addEventListener("gesturestart", gesteAbfangen, { passive: false });
  document.addEventListener("gesturechange", gesteAbfangen, { passive: false });
  document.addEventListener("gestureend", gesteAbfangen, { passive: false });

  // Zwei Finger auf dem Schirm heißt immer: jemand will zoomen. In dieser App
  // gibt es nichts zu zoomen.
  document.addEventListener(
    "touchmove",
    (e) => {
      if (e.touches.length > 1) e.preventDefault();
    },
    { passive: false }
  );

  // ── 2. Doppeltipp-Zoom (iOS)
  //
  // `touch-action: pan-x pan-y` erledigt das auf Android; iOS zoomt trotzdem,
  // wenn zweimal schnell auf DIESELBE Stelle getippt wird.
  //
  // Die Ortsprüfung ist entscheidend und war beim ersten Versuch nicht drin:
  // Ohne sie hätte jeder zweite schnelle Tipp GESCHLUCKT werden können — auch
  // zwei Tipps auf verschiedene Knöpfe. Wer eine Packliste zügig abhakt, tippt
  // im Sekundentakt, und der zweite Haken wäre wirkungslos geblieben. Ein
  // Doppeltipp-Zoom ist nur ein Doppeltipp-Zoom, wenn beide Tipps am selben Ort
  // landen.
  let letzterTipp = 0;
  let letzterOrt = { x: 0, y: 0 };

  document.addEventListener(
    "touchend",
    (e) => {
      const jetzt = Date.now();
      const t = e.changedTouches[0];
      if (!t) return;

      const nah =
        Math.abs(t.clientX - letzterOrt.x) < 30 && Math.abs(t.clientY - letzterOrt.y) < 30;

      if (jetzt - letzterTipp < 300 && nah) {
        e.preventDefault(); // Das war der Zoom-Doppeltipp.
      }

      letzterTipp = jetzt;
      letzterOrt = { x: t.clientX, y: t.clientY };
    },
    { passive: false }
  );

  // ── 3. Kontextmenü (langer Druck / Rechtsklick)
  //
  // Nur dort unterdrücken, wo es nichts zu holen gibt. In Eingabefeldern muss
  // "Einfügen" weiter funktionieren — sonst könnte niemand ein Passwort aus der
  // Zwischenablage einsetzen.
  document.addEventListener("contextmenu", (e) => {
    const t = e.target as HTMLElement | null;
    if (!t) return;
    const istEingabe =
      t.tagName === "INPUT" ||
      t.tagName === "TEXTAREA" ||
      t.isContentEditable ||
      !!t.closest(".select-all, .select-text");
    if (!istEingabe) e.preventDefault();
  });

  // ── 4. Datei ins Fenster gezogen
  //
  // Ohne das öffnet der Browser die Datei und die App ist weg — mitten in der
  // Tour, mit einer Unterschrift, die noch nicht gespeichert ist.
  for (const typ of ["dragover", "drop"] as const) {
    window.addEventListener(typ, (e) => e.preventDefault());
  }

  // ── 5. Die Tastatur
  //
  // Auf iOS schiebt die Tastatur das Layout NICHT hoch — sie legt sich darüber.
  // `100dvh` bleibt die volle Höhe, und der Speichern-Knopf am unteren Rand eines
  // Blattes liegt dann hinter der Tastatur. Man tippt einen Preis ein und kommt
  // nicht mehr an "Speichern".
  //
  // `visualViewport` meldet, wie viel Platz WIRKLICH noch sichtbar ist. Daraus
  // werden zwei CSS-Variablen:
  //   --app-h  die tatsächlich sichtbare Höhe (statt 100dvh)
  //   --tastatur  wie hoch die Tastatur gerade ist (0, wenn geschlossen)
  const vv = window.visualViewport;
  const messen = () => {
    const sichtbar = vv?.height ?? window.innerHeight;
    const diff = Math.max(0, window.innerHeight - sichtbar);

    // ══ Nur eine ECHTE Tastatur darf zählen ══
    //
    // Auf iOS als installierte App ist `visualViewport.height` dauerhaft ~20–40 px
    // kleiner als `innerHeight` — das ist die Home-Indicator-Zone unten, KEINE
    // Tastatur. Nahm man diese Differenz für `--app-h`, endete die App genau um
    // diese Zone zu früh: ein toter, leerer Streifen unter der unteren Leiste.
    // Und `--tastatur` schob grundlos, obwohl nie jemand tippte.
    //
    // Eine Tastatur ist immer mehrere hundert Pixel hoch. Alles unter 100 px ist
    // die Safe-Area und wird ignoriert.
    const tastatur = diff > 100 ? diff : 0;

    // Ohne Tastatur die VOLLE Höhe bis zum physischen Rand — dann schließt die
    // untere Leiste bündig ab und das Bottom-Padding (safe-area-inset-bottom)
    // legt sich sauber über den Home-Indicator. Mit Tastatur die sichtbare Höhe,
    // damit der Speichern-Knopf nicht dahinter verschwindet.
    const hoehe = tastatur ? sichtbar : window.innerHeight;

    const w = document.documentElement.style;
    w.setProperty("--app-h", `${hoehe}px`);
    w.setProperty("--tastatur", `${tastatur}px`);
  };

  messen();
  vv?.addEventListener("resize", messen);
  vv?.addEventListener("scroll", messen);
  window.addEventListener("orientationchange", () => setTimeout(messen, 200));
}
